import { gql } from "@apollo/client";

// Main query for revenue and ticket trends timeseries data
export const GET_TRENDS_REVENUE_DATA = gql`
  query getRevenueForAdmin($startYear: Int!, $endYear: Int!, $startMonth: String!, $endMonth: String!) {
    getRevenueForAdmin(args: {endMonth:$endMonth, endYear:$endYear, startMonth:$startMonth, startYear:$startYear}) {
      monthsName
      revenue
      totalTickets
    }
    getRevenueForAdminAggregate(args: {endMonth:$endMonth, endYear:$endYear, startMonth:$startMonth, startYear:$startYear}) {
      overAllTickets
      overAllSales
      quickasystFee
      avgListPrice
    }
  }
`;

// Query for super user dashboard revenue
export const GET_TRENDS_SUPER_USER_DATA = gql`
  query RevenuesSuperUserDashboard($startYear: Int!, $endYear: Int!, $startMonth: String!, $endMonth: String!, $userId: uuid!) {
    getRevenueForSuperUser(args: { endMonth: $endMonth, endYear: $endYear, startMonth: $startMonth, startYear: $startYear, user_id: $userId }) {
      monthsName
      revenue
      totalTickets
    }
    getRevenueForSuperUserAggregate(args: { endMonth: $endMonth, endYear: $endYear, startMonth: $startMonth, startYear: $startYear, user_id: $userId }) {
      overAllTickets
      overAllSales
      avgListPrice
      quickasystFee
    }
  }
`;

// Query for pending tickets and payout
export const GET_TRENDS_PENDING_DATA = gql`
  query getPendingTickets($limit: Int, $offset: Int, $userId: uuid!, $userRole: String!) {
    getPendingActiveTicketsByLeague(args: { user_id: $userId, user_role: $userRole }, limit: $limit, offset: $offset, order_by: { activeTickets: desc }) {
      leagueName
      activeTickets
    }
    getPendingActiveTicketsCountAndPayout(args: { user_id: $userId, user_role: $userRole }) {
      totalCount
      potentialPayout
    }
  }
`;

// Query for top sales by league
export const GET_TRENDS_TOP_SALES = gql`
  query getTopSalesTickets($month: String!, $year: String!, $userId: uuid!, $userRole: String!) {
    getTopSales(args: { month: $month, year: $year, user_id: $userId, user_role: $userRole }) {
      leagueName
      totalCounts
    }
  }
`;

// Query for list and sold tickets breakdown
export const GET_TRENDS_TICKET_STATS = gql`
  query getTicketStats($leagueId: uuid, $status: String) {
    tickets(where: {league_id: {_eq: $leagueId}, status: {_eq: $status}}, limit: 1000) {
      id
      status
      price
      list_price
      league_id
      created_at
      updated_at
    }
    ticketsAggregate: tickets_aggregate(where: {league_id: {_eq: $leagueId}}) {
      aggregate {
        count
      }
    }
  }
`;

// Query for all leagues data
export const GET_TRENDS_LEAGUES = gql`
  query getLeagues {
    leagues(limit: 50) {
      id
      name
      display_name
    }
  }
`;

// Query for marketplace / channel data - SOLD TICKETS ONLY
export const GET_TRENDS_SALES_CHANNELS = gql`
  query getTicketsByChannel(
    $startdate: timestamptz
    $enddate: timestamptz
    $pageSize: Int = 1000
    $pageOffset: Int = 0
  ) {
    getmanageticketwithvlids(
      where: {
        tp_updated_at: { _gte: $startdate, _lte: $enddate }
        tp_status: { _ilike: "%sold%" }
      }
      limit: $pageSize
      offset: $pageOffset
      order_by: { tp_updated_at: desc }
    ) {
      id: tp_id
      listing_id: tp_vl_listing_id
      channel: tp_marketplace_name
      price: tp_logitix_amount
      paid: tp_sold_amount
      profit: tp_quick_cut_amount
      status: tp_status
      created_at: tp_updated_at
    }
  }
`;

export const GET_TRENDS_TICKET_TIMELINE = gql`
  query GetTrendsTicketTimeline(
    $startdate: date
    $enddate: date
    $pageSize: Int = 1000
    $pageOffset: Int = 0
  ) {
    filterlistticketswithvlids: filterlisttickets(
      args: {
        array_tpid: null
        enddate: $enddate
        leagueid: null
        payment_status: null
        startdate: $startdate
        ticket_status: null
        ticketid: null
        tpid: null
      }
      where: {_or:[{e_name:{_ilike:"%%"}},{full_name:{_ilike:"%%"}},{u_email_id:{_ilike:"%%"}}]}
      limit: $pageSize
      offset: $pageOffset
      order_by: { tp_updated_at: desc }
    ) {
      latest_update: tp_updated_at
      event_date: e_date
      event_name: e_name
      league_name: l_name
      section: tp_section
      row: tp_row
      seat_range: tp_seat_no
      tp_list_price
      tp_logitix_amount
      tp_mobile_display_status: tp_status
      tp_sold_amount
      tp_status
      user_id: u_id
      user_name: full_name
      user_email: u_email_id
      ticket_id: t_id
    }
  }
`;

export const GET_USER_PRICING_MODES = gql`
  query GetUserPricingModes($userIds: [uuid!]) {
    user(where: { u_id: { _in: $userIds } }) {
      u_id
      u_pricing_mode
      u_client_type
    }
  }
`;

export interface TrendsChartDataPoint {
  date: string;
  revenue: number;
  profit: number;
  sold: number;
  price: number;
  qaProfit: number;
  userPayout: number;
  vlFee: number;
  pending: number;
  listed: number;
  verified: number;
  toBeVerified: number;
  delisted: number;
  unsold: number;
}

export interface LeagueSalesData {
  name: string;
  revenue: number;
  tickets: number;
  color: string;
}

export interface MarketplaceData {
  name: string;
  revenue: number;
  tickets: number;
  color: string;
}

export interface TrendsDataAggregate {
  overAllTickets: number;
  overAllSales: number;
  quickasystFee: number;
  avgListPrice: number;
}

export interface TrendsTimelineTicket {
  latest_update: string;
  event_date?: string | null;
  event_name?: string | null;
  league_name: string;
  section?: string | null;
  row?: string | null;
  seat_range?: string | null;
  payment_status: string | null;
  payout_status: string | null;
  total_count: number;
  tp_list_price: number | null;
  tp_logitix_amount: number | null;
  tp_mobile_display_status: string | null;
  tp_sold_amount: number | null;
  tp_status: string;
  tp_vl_listing_id?: string | null;
  tp_vl_fee?: number | null;
  tp_commission?: number | null;
  tp_affiliate_payment?: number | null;
  tp_other_fees?: number | null;
  victory_live_fee?: number | null;
  sales_rep_commission?: number | null;
  affiliate_payment?: number | null;
  other_fees?: number | null;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  ticket_id: string;
  grouped_ticket_data?: Array<{ tp_id?: string }> | null;
}

export interface UserPricingModeRow {
  u_id: string;
  u_pricing_mode?: string | null;
  u_client_type?: string | null;
}

export interface TrendsChannelTicket {
  id: string;
  listing_id?: string | null;
  channel: string | null;
  price: number | null;
  paid?: number | null;
  profit?: number | null;
  status: string | null;
  created_at: string;
}
